import React from 'react';
import { Sprout, Sun, Handshake } from 'lucide-react';

/**
 * ProtocolOfHarmony — the cooperative-chemistry manifesto.
 *
 * Surfaces on /story (and Home) as the natural philosophical
 * companion to <VedicBiophysics />: where biophysics names *what*
 * we measure, this section names *how* the molecules behave with
 * each other. Three actors, one ritual:
 *
 *   I.  Onion Seed       — native sulfur, follicle revival
 *   II. Rice Bran        — ferulic acid, UV armor
 *   III. Sunflower Lecithin — "The Peacemaker", oil ⇄ water bridge
 *
 * Closing strophe ties the chemistry to the human-and-planetary act:
 * cooperation in the bottle becomes cooperation on the planet — the
 * ultimate goal of our Seva.
 */

const ACTORS = [
  {
    id: 'onion-seed',
    icon: Sprout,
    overline: 'Pillar I · The native sulfur',
    title: 'Onion Seed.',
    body:
      "Allium cepa carries a measurable sulfur signature — quercetin, S-allyl cysteine, organosulfides — that the follicle reads as a permission slip to wake again. It does not try to be Rice Bran. It is the keeper of the root.",
    chip: 'Sulfur · follicle revival',
  },
  {
    id: 'rice-bran',
    icon: Sun,
    overline: 'Pillar II · The UV armor',
    title: 'Rice Bran.',
    body:
      "Ferulic acid and γ-oryzanol — the same molecules a grain of rice uses to shield its own embryo from the noon sun — translate directly to a strand of hair. Rice Bran does not try to be Onion Seed. It is the keeper of the canopy.",
    chip: 'Ferulic acid · UV defense',
  },
  {
    id: 'sunflower-lecithin',
    icon: Handshake,
    overline: 'Pillar III · The Peacemaker',
    title: 'Sunflower Lecithin.',
    body:
      "Phosphatidylcholine, the molecule that builds every living cell membrane, has a head that loves water and a tail that loves oil. In cold-process physics — no heat, no force — Sunflower Lecithin walks into the divide between Onion Seed (oil) and Kewra Water and quietly negotiates a peace. The emulsion is the treaty.",
    chip: 'Cold-process · oil ⇄ water bridge',
    peacemaker: true,
  },
];

const ProtocolOfHarmony = ({ testId = 'protocol-of-harmony', variant = 'light' }) => {
  const isDark = variant === 'dark';
  const bg     = isDark ? 'bg-[#0E1A22] text-[#F4F8F9]'  : 'bg-[#F4F8F9] text-[#7C9CB1]';
  const muted  = isDark ? 'text-[#F4F8F9]/80'            : 'text-[#7C9CB1]/85';
  const accent = isDark ? 'text-[#F4F8F9]/65'            : 'text-[#90B0BF]';
  const rule   = isDark ? 'border-[#F4F8F9]/20'          : 'border-[#C1D7E2]';
  const cardBg = isDark ? 'bg-[#0E1A22] border-[#F4F8F9]/25' : 'bg-[#F4F8F9] border-[#C1D7E2]';

  return (
    <section
      id="protocol-of-harmony"
      data-testid={testId}
      className={`relative px-6 md:px-12 py-24 md:py-32 border-t ${rule} ${bg}`}
    >
      <div className="max-w-[1400px] mx-auto">
        {/* The Biological Ceasefire — the manifesto's binary truth */}
        <div
          data-testid={`${testId}-ceasefire`}
          className={`mb-16 md:mb-20 px-7 md:px-12 py-9 md:py-12 border ${isDark ? 'border-[#D4AF5A]/40 bg-[#D4AF5A]/[0.06]' : 'border-[#D4AF5A]/55 bg-[#D4AF5A]/[0.08]'} relative`}
        >
          <span
            aria-hidden
            className="absolute top-0 left-10 right-10 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
            }}
          />
          <p
            data-testid={`${testId}-ceasefire-overline`}
            className="font-mono text-[10px] md:text-[11px] tracking-[0.36em] uppercase font-semibold text-[#D4AF5A] mb-4 flex items-center gap-3"
          >
            <span aria-hidden className="inline-block w-8 h-px bg-[#D4AF5A]" />
            The Biological Ceasefire
          </p>
          <p
            data-testid={`${testId}-ceasefire-statement`}
            className={`font-serif font-light leading-[1.05] tracking-tight ${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'}`}
            style={{ fontSize: 'clamp(28px, 4.5vw, 64px)' }}
          >
            We do not alter your nature.
            <br />
            We <span className="text-[#D4AF5A] italic">armor</span> it.
          </p>
        </div>

        {/* Overline + title */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
          <div className="md:col-span-7">
            <p
              data-testid={`${testId}-overline`}
              className={`font-mono text-[11px] md:text-[12px] tracking-[0.34em] uppercase font-semibold ${accent} mb-6 flex items-center gap-3`}
            >
              <span
                aria-hidden
                className="inline-block w-10 h-[2px] bg-[#D4AF5A]"
              />
              The Manifesto · § II
            </p>
            <h2
              data-testid={`${testId}-title`}
              className={`font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02] ${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'}`}
            >
              The Protocol
              <br />
              of <em className={accent}>Harmony</em>.
            </h2>
          </div>

          <div className="md:col-span-5 md:pt-4">
            <p
              data-testid={`${testId}-thesis`}
              className={`font-serif italic text-lg md:text-xl leading-relaxed max-w-md ${muted}`}
            >
              In our formulations, no single botanical tries to be what it
              is not. The native sulfur of the Onion Seed does not compete
              with the UV armor of the Rice Bran; they{' '}
              <span className="not-italic font-medium text-[#D4AF5A]">
                cooperate
              </span>
              . They come together as a unified matrix to create one whole
              product that helps. We believe humans must do the same — this
              is the ultimate goal of our Seva.
            </p>
          </div>
        </div>

        {/* Three actors */}
        <ol
          data-testid={`${testId}-actors`}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {ACTORS.map((a) => {
            const Icon = a.icon;
            return (
              <li
                key={a.id}
                data-testid={`${testId}-actor-${a.id}`}
                className={`relative border p-7 md:p-9 flex flex-col ${cardBg} ${a.peacemaker ? 'md:-translate-y-2' : ''}`}
              >
                {/* Gold thread along the top */}
                <span
                  aria-hidden
                  className="absolute top-0 left-7 right-7 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                  }}
                />
                <p
                  className={`font-mono text-[10px] tracking-[0.3em] uppercase ${accent} mb-5`}
                >
                  {a.overline}
                </p>
                <Icon
                  size={26}
                  strokeWidth={1.2}
                  className={`${a.peacemaker ? 'text-[#D4AF5A]' : isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'} mb-5`}
                />
                <h3
                  className={`font-serif text-2xl md:text-3xl font-light leading-tight ${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'}`}
                >
                  {a.title}
                </h3>
                <p className={`mt-4 text-sm md:text-base font-light leading-relaxed ${muted}`}>
                  {a.body}
                </p>
                <span
                  data-testid={`${testId}-actor-${a.id}-chip`}
                  className={`mt-6 inline-flex items-center gap-2 self-start font-mono text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border ${a.peacemaker ? 'border-[#D4AF5A]/60 text-[#D4AF5A]' : isDark ? 'border-[#F4F8F9]/30 text-[#F4F8F9]/80' : 'border-[#C1D7E2] text-[#7C9CB1]/80'}`}
                >
                  <span
                    aria-hidden
                    className={`inline-block w-1.5 h-1.5 rounded-full ${a.peacemaker ? 'bg-[#D4AF5A]' : isDark ? 'bg-[#F4F8F9]/60' : 'bg-[#90B0BF]'}`}
                  />
                  {a.chip}
                </span>
              </li>
            );
          })}
        </ol>

        {/* Bridge — the chemistry → the planetary act */}
        <div
          data-testid={`${testId}-bridge`}
          className={`mt-16 md:mt-20 pt-10 border-t ${rule} grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16`}
        >
          <div className="md:col-span-7">
            <p className={`font-mono text-[10px] tracking-[0.3em] uppercase ${accent} mb-4`}>
              <span className="inline-block w-7 h-px bg-[#D4AF5A] mr-3 align-middle" />
              The Analogy · The Goal of Seva
            </p>
            <p
              data-testid={`${testId}-bridge-body`}
              className={`font-serif text-xl md:text-2xl leading-snug ${muted}`}
            >
              Cooperative chemistry is the molecular signature of a
              cooperative species. When a formula refuses to let any one
              ingredient dominate the others, it rehearses — in miniature —
              the larger refusal the planet is asking us to learn.{' '}
              <span className={`not-italic font-medium ${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'}`}>
                Cooperation in the bottle becomes cooperation on the
                planet. This is the ultimate goal of our Seva.
              </span>
            </p>
          </div>
          <aside className="md:col-span-5">
            <p
              data-testid={`${testId}-signature`}
              className={`font-mono text-[10px] tracking-[0.3em] uppercase ${accent} leading-relaxed`}
            >
              Onion Seed keeps the root ·
              <br />
              Rice Bran keeps the canopy ·
              <br />
              Sunflower Lecithin holds <span className="text-[#D4AF5A]">the matrix</span>.
            </p>
            <p className={`mt-6 font-serif italic ${muted}`}>
              The Vedas named the cooperation. The spectrometer confirms
              it. Atreya prescribes the hour.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default ProtocolOfHarmony;
